---
title: 'Global Privacy Control: Why Ignoring the Signal Is Now a Violation'
description: 'Twelve US states require businesses to honour universal opt-out signals. It is invisible when broken, trivial for a regulator to test, and most sites are failing it silently.'
slug: 'global-privacy-control-compliance'
publishedAt: '2026-11-09'
author: 'Syrcha'
tags: ['CCPA', 'US Privacy', 'Global Privacy Control']
readingMinutes: 7
draft: true
---

Most privacy obligations announce themselves. A missing privacy policy is visible. A broken consent banner is visible. Ignoring a Global Privacy Control signal is visible to nobody except a regulator, who can check it from the outside in about ten seconds.

Twelve states now require businesses to honour universal opt-out mechanisms. This is the obligation most likely to be failing on your site right now without anyone knowing.

## What GPC is

Global Privacy Control is a browser-level signal expressing a user's decision to opt out of the sale and sharing of their personal data. The user configures it once, in a browser that supports it natively or via an extension, and it's transmitted to every site they visit.

Technically it arrives two ways. As an HTTP request header:

```http
Sec-GPC: 1
```

And exposed to JavaScript on the page:

```js
navigator.globalPrivacyControl // true when the user has enabled GPC
```

The design intent is to remove the absurdity of asking every user to hunt for a footer link on every site they visit. They set the preference once; sites obey it.

### A caveat on browser support

Native support is still thin. Brave sends GPC by default. Firefox has supported it since version 120, though the user has to switch it on in privacy settings. DuckDuckGo's browser sends it. Chrome, Safari and Edge don't implement it at all, so users on those browsers need an extension like Privacy Badger or OptMeowt.

Two consequences for your implementation.

First, `navigator.globalPrivacyControl` is `undefined` on most browsers, not `false`. Absence of the property means the browser has no opinion, not that the user declined to opt out. Branch on the value being exactly `true`, and don't treat `undefined` as a signal either way.

Second, the volume of GPC traffic you see will be low, and that's not evidence the implementation works. A regulator testing you will be running one of the browsers that does send it.

That thinness has an expiry date. California's AB 566, the Opt Me Out Act, signed in October 2025, requires browsers sold in California to include a built-in opt-out setting from **1 January 2027**. Chrome and Safari either ship GPC or something equivalent, and the share of traffic carrying the signal stops being a rounding error.

## Where it is mandatory

Twelve states require recognition of universal opt-out mechanisms: **California, Colorado, Connecticut, Delaware, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon and Texas.**

The obligation isn't "acknowledge the signal" or "mention it in your privacy policy". It's to treat it as an opt-out request from that consumer and apply it automatically.

Specifically, you may not:

- Show a banner asking them to opt out again, as though the signal hadn't arrived
- Require them to also find and use your [Do Not Sell or Share link](/blog/do-not-sell-my-personal-information-link)
- Apply it on one page and not others
- Record the preference without acting on it

## Why sites fail it silently

Three reasons, and they compound.

**Nothing reports it.** Your analytics won't tell you a GPC-enabled visitor arrived and got tracked anyway. No error, no console warning, no dashboard metric. The failure produces no signal of its own.

**The consent tool and the tag stack are configured separately.** Many consent platforms can detect GPC, but detection has to be wired to actually gate the tags. Teams enable the setting, see it acknowledged in the vendor UI, and assume the job is done.

**It's a header, not a click.** Teams reason about consent in terms of banner interactions. A signal arriving before the page renders, without any user action on your site, doesn't fit that mental model and gets overlooked.

## What enforcement looks like

California's Sephora settlement in 2022 is the clearest precedent. The finding turned on failure to honour GPC signals, and carried a 1.2 million dollar penalty alongside injunctive terms and compliance reporting.

State attorneys general have since named GPC compliance an enforcement priority, and the California Privacy Protection Agency has issued guidance on it.

Why it attracts enforcement attention is efficiency. Most privacy investigations need cooperation, document requests and internal records. Testing GPC needs a browser extension and a network tab. A regulator can survey hundreds of sites in an afternoon, and the ones that fail are self-selecting.

## How to test yours in five minutes

1. **Install a GPC-enabled browser or extension** and confirm the signal is active.
2. **Load your site in a private window** so no prior consent cookie interferes.
3. **Open the network tab** and filter for third-party requests.
4. **Watch what fires.** Advertising pixels, retargeting tags, data-broker beacons. If these still run, you're not honouring the signal.
5. **Check a deep page**, not just the homepage. Application has to be consistent across the site.
6. **Confirm no banner asks again.** Prompting a GPC user to opt out is itself an indication the signal wasn't applied.

Verify the signal is arriving at all, separately. Log the `Sec-GPC` header at the edge for a day and check it's non-zero. If your CDN or cache strips it before it reaches your application, nothing downstream can act on it. That's a genuinely common failure and entirely invisible from the front end.

## Implementing it properly

**Detect early.** Read the header server-side, before rendering, so the decision is made before any tag can initialise. Client-side-only detection races against your own scripts.

**Gate, don't unload.** Treat GPC exactly like a refusal from your consent tool: tags never initialise. Loading trackers and then trying to disable them leaves cookies already written.

**Watch your caching.** With pages cached at the edge, a cached response generated for a non-GPC user can be served to a GPC user. Vary on the header, or handle the decision client-side before tags load, or exclude affected responses from shared caches.

**Propagate downstream.** The opt-out has to reach vendors who would otherwise receive the data, not merely stop your own pixel from firing.

**Don't overreach.** GPC is an opt-out of sale and sharing. It isn't an instruction to disable functionality the user asked for, and treating it as a blanket "break the site" switch serves nobody.

## The takeaway

GPC is mandatory in a dozen states, cheap to implement, invisible when broken, and testable by anyone from outside your systems.

Spend five minutes with a GPC-enabled browser and your network tab open. If the advertising tags still fire, that's a live violation in twelve states, and it'll keep being one until someone checks.

For the broader picture, see our guide to [US state privacy laws in 2026](/blog/us-state-privacy-laws-2026).

---

_This article is general information, not legal advice._

**Sources:** [Global Privacy Control specification](https://globalprivacycontrol.org/) · [MDN: Navigator.globalPrivacyControl](https://developer.mozilla.org/en-US/docs/Web/API/Navigator/globalPrivacyControl) · [Tannenbaum Helpern: Universal opt-out mechanisms and Global Privacy Control](https://www.thsh.com/publications/universal-opt-out-mechanisms-and-global-privacy-control-state-law-requirements-and-compliance-guidance/) · [California Privacy Protection Agency](https://cppa.ca.gov/)
